/**
 * Modern Login page
 */

import { Link, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Mail, Lock, ShieldCheck } from 'lucide-react';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { useLogin } from '../../hooks/useAuth';
import { ApiError } from '../../lib/api';
import { useSEO, SEO_CONFIGS } from '../../hooks/useSEO';

const loginSchema = z.object({
  email: z.string().email('Email invalido'),
  password: z.string().min(1, 'Senha obrigatoria'),
  mfaCode: z.string().min(6, 'Código MFA inválido').max(16, 'Código MFA inválido').optional(),
});

type LoginForm = z.infer<typeof loginSchema>;

export default function LoginPage() {
  // SEO meta tags for login page
  useSEO(SEO_CONFIGS.login);

  const location = useLocation();
  const fromState = (location.state as { from?: { pathname?: string } } | undefined)?.from?.pathname;
  const redirectTo = fromState && fromState.startsWith('/') ? fromState : undefined;

  const login = useLogin({ redirectTo });
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = (data: LoginForm) => {
    const maybeMfaRequired =
      login.error instanceof ApiError &&
      !!(login.error.data as { mfaRequired?: boolean } | undefined)?.mfaRequired;

    if (maybeMfaRequired && !data.mfaCode) {
      setError('mfaCode', { type: 'required', message: 'Informe o código do seu autenticador' });
      return;
    }

    login.mutate(data);
  };

  const mfaRequired =
    login.error instanceof ApiError &&
    !!(login.error.data as { mfaRequired?: boolean } | undefined)?.mfaRequired;

  return (
    <div>
      <div className="text-center mb-8">
        <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">Bem-vindo de volta!</h1>
        <p className="text-neutral-600 dark:text-neutral-400 mt-2">Entre na sua conta para continuar</p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        <Input
          {...register('email')}
          type="email"
          placeholder="Seu email"
          icon={<Mail className="w-5 h-5" />}
          error={errors.email?.message}
          autoComplete="email"
        />

        <Input
          {...register('password')}
          type="password"
          placeholder="Sua senha"
          icon={<Lock className="w-5 h-5" />}
          error={errors.password?.message}
          autoComplete="current-password"
          showPasswordToggle
        />

        {mfaRequired ? (
          <Input
            {...register('mfaCode')}
            type="text"
            inputMode="numeric"
            placeholder="Código do autenticador (MFA)"
            icon={<ShieldCheck className="w-5 h-5" />}
            error={errors.mfaCode?.message}
            autoComplete="one-time-code"
          />
        ) : null}

        <div className="flex items-center justify-between text-sm">
          <label className="flex items-center gap-2 cursor-pointer group">
            <input
              type="checkbox"
              className="w-4 h-4 rounded border-neutral-300 dark:border-neutral-600 text-primary-500 focus:ring-primary-500 dark:bg-neutral-800"
            />
            <span className="text-neutral-600 dark:text-neutral-400 group-hover:text-neutral-900 dark:group-hover:text-neutral-200 transition-colors">
              Lembrar de mim
            </span>
          </label>
          <Link
            to="/recuperar-senha"
            className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium transition-colors"
          >
            Esqueci minha senha
          </Link>
        </div>

        <Button
          type="submit"
          className="w-full"
          loading={login.isPending}
        >
          Entrar
        </Button>
      </form>

      <div className="relative my-8">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-neutral-200 dark:border-neutral-700" />
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="px-4 bg-white dark:bg-neutral-900 text-neutral-500 dark:text-neutral-400">
            ou
          </span>
        </div>
      </div>

      <p className="text-center text-neutral-600 dark:text-neutral-400">
        Não tem uma conta?{' '}
        <Link
          to="/cadastro"
          className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium transition-colors"
        >
          Cadastre-se
        </Link>
      </p>
    </div>
  );
}
